import * as React from 'react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/utils';

export interface FieldControlState {
    describedBy?: string;
    invalid: boolean;
    required: boolean;
}

export const FieldControlContext = React.createContext<FieldControlState | null>(null);

export function useFieldControl() {
    return React.useContext(FieldControlContext);
}

const fieldErrorClasses =
    '[&_input]:ring-2 [&_input]:ring-destructive [&_button[role=combobox]]:ring-2 [&_button[role=combobox]]:ring-destructive';

export function Field({
    label,
    children,
    hint,
    error,
    invalid,
    describedBy,
    required,
    as = 'field',
    className,
    legendClassName,
}: {
    label: string;
    children: ReactNode;
    hint?: ReactNode;
    error?: string;
    invalid?: boolean;
    describedBy?: string;
    required?: boolean;
    as?: 'field' | 'group';
    className?: string;
    legendClassName?: string;
}) {
    const messageId = React.useId();
    const message = error ?? hint;
    const describedByIds =
        [message ? messageId : null, describedBy].filter(Boolean).join(' ') || undefined;
    const isInvalid = invalid ?? Boolean(error);
    const requiredIndicator = required ? (
        <span className="text-destructive" aria-hidden="true">
            {' '}
            *
        </span>
    ) : null;
    const messageElement = error ? (
        <p id={messageId} className="text-xs text-destructive" role="alert">
            {error}
        </p>
    ) : hint ? (
        <p id={messageId} className="text-xs text-neutral-600">
            {hint}
        </p>
    ) : null;

    if (as === 'group') {
        return (
            <fieldset
                className={cn('min-w-0 space-y-1', className)}
                aria-describedby={describedByIds}
                data-invalid={isInvalid ? 'true' : undefined}
            >
                <legend
                    className={cn(
                        'mb-1 p-0 text-sm font-semibold text-neutral-950',
                        legendClassName,
                    )}
                >
                    {label}
                    {requiredIndicator}
                </legend>
                <FieldControlContext.Provider value={null}>{children}</FieldControlContext.Provider>
                {messageElement}
            </fieldset>
        );
    }

    return (
        <div className={cn('space-y-1', isInvalid && fieldErrorClasses, className)}>
            <label className="block space-y-1">
                <span className="text-sm font-semibold text-neutral-950">
                    {label}
                    {requiredIndicator}
                </span>
                <FieldControlContext.Provider
                    value={{
                        describedBy: describedByIds,
                        invalid: isInvalid,
                        required: Boolean(required),
                    }}
                >
                    {children}
                </FieldControlContext.Provider>
            </label>
            {messageElement}
        </div>
    );
}

export function FormErrorAlert({ message }: { message: string }) {
    const ref = React.useRef<HTMLParagraphElement>(null);

    React.useEffect(() => {
        ref.current?.scrollIntoView?.({
            block: 'nearest',
            behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
                ? 'auto'
                : 'smooth',
        });
    }, [message]);

    return (
        <p ref={ref} className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert">
            {message}
        </p>
    );
}

export function useFocusFirstInvalid(containerRef: React.RefObject<HTMLElement>): () => void {
    const [requestCount, setRequestCount] = React.useState(0);

    React.useEffect(() => {
        if (requestCount === 0) return;
        const candidates = containerRef.current?.querySelectorAll<HTMLElement>(
            '[aria-invalid="true"], fieldset[data-invalid="true"]',
        );
        let firstInvalidControl: HTMLElement | null = null;
        for (const candidate of candidates ?? []) {
            firstInvalidControl = candidate.matches('[aria-invalid="true"]')
                ? candidate
                : (candidate.querySelector<HTMLElement>('[aria-invalid="true"]') ??
                  candidate.querySelector<HTMLElement>('input:not([disabled])'));
            if (firstInvalidControl) break;
        }
        firstInvalidControl?.focus();
    }, [containerRef, requestCount]);

    return React.useCallback(() => setRequestCount((current) => current + 1), []);
}
